import { useMemo, useCallback } from 'react'
import { ArrowRightOutlined, CheckCircleFilled } from '@ant-design/icons'
import { Card, Alert } from 'antd'
import { useParams } from 'react-router-dom'
import {
    ESTIMATED_DELIVERY_DATE_DISPLAY,
    IN_PRODUCTION_STATUS,
    ORDER_COMPLETE_STATE,
    ORDER_RECEIVED_STATE_DISPLAY,
    PENDING_PRODUCTION_STATUS,
    PENDING_PRODUCTION_DISPLAY,
    PARTIAL_SHIPPING_STATUS,
    READY_SHIPPING_STATUS,
    CANCELED_STATUS,
    DELIVERY_STATUSES,
    IN_PRODUCTION_DISPLAY,
    PRODUCTION_COMPLETE_DISPLAY,
    DELIVERED_DISPLAY,
    PARTIAL_SHIPPING_MULTI_SHIPMENT_TEXT,
    PARTIAL_SHIPPING_PART_STATUS,
    SHIPPED_DISPLAY,
} from '@Orders/constants'
import styles from '@Orders/OrderDetailsPage/styles.css'
import {
    salesOrder_salesOrder_shipments_items,
    salesOrder_salesOrder_salesOrderLines_items,
} from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { useOrderDetailData } from '@Orders/logic'
import { BORDER } from '@User/MyAccountPage/styleConstants'

import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import { NOT_AVAILABLE } from '../../constants'

import { getOrderStepsDateFormat, useShippedString } from './logic'

type currentStep = {
    [key: string]: number
}

type Params = {
    orderName: string
}

const CURRENT_STEP_NEW: currentStep = {
    [PENDING_PRODUCTION_STATUS]: 0,
    [IN_PRODUCTION_STATUS]: 1,
    [PARTIAL_SHIPPING_STATUS]: 2,
    [READY_SHIPPING_STATUS]: 2,
    [ORDER_COMPLETE_STATE]: 3,
}

export default function SalesOrderSteps(props: {
    deliveryStatus: string
    shippedBy: string
    fictivEstimatedDeliveryDate: any | null
    shipments: salesOrder_salesOrder_shipments_items[]
    salesOrderLines: salesOrder_salesOrder_salesOrderLines_items[]
}) {
    const {
        deliveryStatus,
        shippedBy,
        fictivEstimatedDeliveryDate,
        shipments,
        salesOrderLines,
    } = props

    const { orderName = '' } = useParams<Params>()

    const { data } = useOrderDetailData(orderName)

    const estimatedShipDate = getOrderStepsDateFormat(shippedBy)

    const createdAt = data?.salesOrder?.createdAt
    const estimatedShipDateAfterDag =
        shippedBy > createdAt ? estimatedShipDate : NOT_AVAILABLE
    const shippedString = useShippedString(shipments, salesOrderLines)
    const shipmentsDelivered = useMemo(
        () =>
            deliveryStatus !== PARTIAL_SHIPPING_STATUS &&
            shipments.every(
                shipment =>
                    shipment?.trackingData?.status ===
                    DELIVERY_STATUSES.DELIVERED,
            ),
        [deliveryStatus, shipments],
    )

    const shipmentEstDeliveryDate = useCallback(
        (shipment: salesOrder_salesOrder_shipments_items) => {
            const lastStatus =
                shipment?.trackingData?.trackingDetails.slice(-1)[0]

            const estDeliveryDate =
                lastStatus?.status === DELIVERY_STATUSES.DELIVERED
                    ? lastStatus?.datetime
                    : shipment?.trackingData?.estDeliveryDate

            return estDeliveryDate
                ? getOrderStepsDateFormat(estDeliveryDate, shipment?.carrier)
                : getOrderStepsDateFormat(fictivEstimatedDeliveryDate, null)
        },
        [fictivEstimatedDeliveryDate],
    )

    const firstShipmentEstDeliveryDate = shipmentEstDeliveryDate(shipments[0])
    const isShipmentsEstDateSame = useMemo(() => {
        return (
            deliveryStatus !== PARTIAL_SHIPPING_STATUS &&
            shipments.every(shipment => {
                const estDeliveryDate = shipmentEstDeliveryDate(shipment)

                return estDeliveryDate === firstShipmentEstDeliveryDate
            })
        )
    }, [
        deliveryStatus,
        shipments,
        shipmentEstDeliveryDate,
        firstShipmentEstDeliveryDate,
    ])

    const stepIcon = (currentStep: number) => {
        const checkIcon = (
            <CheckCircleFilled
                style={{
                    color: getColor('success'),
                    fontSize: 20,
                    marginRight: 10,
                }}
            />
        )

        const numberIcon = (
            <Box className={styles.stepNumberStyles}>{currentStep + 1}</Box>
        )

        if (currentStep <= 2 && shipments.length > 0) {
            return checkIcon
        }

        if (shipments.length > 0 && currentStep === 3) {
            return shipmentsDelivered ? checkIcon : numberIcon
        }

        return CURRENT_STEP_NEW[deliveryStatus] >= currentStep ||
            currentStep === 0
            ? checkIcon
            : numberIcon
    }

    if (deliveryStatus === CANCELED_STATUS) {
        return (
            <Alert
                message="This order has been cancelled"
                type="error"
                showIcon
                style={{ marginBottom: 15 }}
            />
        )
    }

    let productionString = PENDING_PRODUCTION_DISPLAY

    if (
        CURRENT_STEP_NEW[deliveryStatus] ===
        CURRENT_STEP_NEW[IN_PRODUCTION_STATUS]
    ) {
        productionString = IN_PRODUCTION_DISPLAY
    } else if (
        CURRENT_STEP_NEW[deliveryStatus] >
        CURRENT_STEP_NEW[IN_PRODUCTION_STATUS]
    ) {
        productionString = PRODUCTION_COMPLETE_DISPLAY
    }

    let deliveredString = ESTIMATED_DELIVERY_DATE_DISPLAY

    if (shipments.length > 0 && shipmentsDelivered) {
        deliveredString = DELIVERED_DISPLAY
    }

    return (
        <Card
            style={{
                borderRadius: '4px',
                marginBottom: '10px',
                border: BORDER,
            }}
        >
            <Box direction="row" alignItems="center">
                <Box
                    margin="10px 20px 10px 0"
                    alignItems="center"
                    style={{ fontWeight: 500 }}
                >
                    {stepIcon(CURRENT_STEP_NEW[PENDING_PRODUCTION_STATUS])}
                    {ORDER_RECEIVED_STATE_DISPLAY}
                </Box>
                <ArrowRightOutlined
                    style={{
                        fontSize: 20,
                        color: getColor('blackAlpha15'),
                    }}
                />
                <Box
                    margin="10px 20px"
                    alignItems="center"
                    style={{ fontWeight: 500 }}
                >
                    {stepIcon(CURRENT_STEP_NEW[IN_PRODUCTION_STATUS])}
                    {productionString}
                </Box>
                <ArrowRightOutlined
                    style={{
                        fontSize: 20,
                        color: getColor('blackAlpha15'),
                    }}
                />
                <Box margin="10px 20px" alignItems="center">
                    {stepIcon(CURRENT_STEP_NEW[READY_SHIPPING_STATUS])}
                    <Box direction="col">
                        <Text fontWeight={500}>{shippedString}</Text>
                        {shippedString !== SHIPPED_DISPLAY && (
                            <Text color={getColor('blackAlpha45')}>
                                {shipments?.length > 1
                                    ? PARTIAL_SHIPPING_PART_STATUS
                                    : estimatedShipDateAfterDag}
                            </Text>
                        )}
                    </Box>
                </Box>
                <ArrowRightOutlined
                    style={{
                        fontSize: 20,
                        color: getColor('blackAlpha15'),
                    }}
                />
                <Box margin="10px 20px" alignItems="center">
                    {stepIcon(CURRENT_STEP_NEW[ORDER_COMPLETE_STATE])}
                    <Box direction="col">
                        <Text fontWeight={500}>{deliveredString}</Text>
                        <Text color={getColor('blackAlpha45')}>
                            {isShipmentsEstDateSame
                                ? firstShipmentEstDeliveryDate
                                : PARTIAL_SHIPPING_MULTI_SHIPMENT_TEXT}
                        </Text>
                    </Box>
                </Box>
            </Box>
        </Card>
    )
}
